import type { CameraRoute } from '@datum-sdk/scene-format';

/**
 * The kaaba preloader flights, as SDK scene data.
 *
 * This is the whole point of the native-route path: the flight is no longer
 * something Pilgrim computes at load time from its own `camera.flyover`
 * dialect, it is a `CameraRoute[]` the scene carries — the same shape a Studio
 * author produces and a backend can serve. Swap this array for one that arrives
 * with the scene and the flights change with no code change at all.
 *
 * **Converted once, the source since.** The first copy came from the legacy
 * `kaabaSceneConfig.camera` (`previewTourStart` / `previewTourEnd` / `flyover`)
 * by `convertLegacyPreloader.ts`, whose test flies a conversion against the
 * legacy curve. From here on the flights are tuned in the SDK editor (`?flight` →
 * Routes → Copy Routes); `sceneRoutes.test.ts` holds them to what the preloader
 * and the scene need, and freezes the legacy twin's flights instead.
 *
 * Four things in here are not obvious and are load-bearing:
 *
 * - **The turntable is four 90° hops, not one 360°.** A full turn normalises to
 *   a zero sweep and is silently skipped (CR C13). `loop: true` then spins it
 *   for as long as the host lets it — the host stops it when the splat lands.
 * - **The enter spins the ring up.** Its second leg is the 0.5 s ramp, an orbit
 *   eased with a pure t², so the loop starts at full speed instead of a jolt.
 * - **The flyover opens with a bare `duration`.** A waypoint that also named a
 *   pose would read as "nothing changed" and collapse to zero, dropping the
 *   1 s pre-reveal hold entirely.
 * - **The flyover is one smooth run of views** at the legacy curve's control
 *   points — the SDK flies the same centripetal Catmull-Rom through them. Each
 *   view's `duration` is when the legacy clock reaches it, and its bezier
 *   carries the speed across the knot (`repaceSmoothRuns.ts`). Do not "round"
 *   the beziers to a preset.
 */
export const KAABA_CAMERA_ROUTES: CameraRoute[] = [
  {
    id: 'preview-enter',
    waypoints: [
      {
        target: [-1.085102176, 0.435600471, 0.466625886],
        lookAt: [-0.32, 0.11, 0.48],
        duration: 1000,
        easing: [0.65, 0, 0.35, 1],
      },
      {
        orbit: {
          radius: 0.83161041,
          azimuth: -2.940854695,
          polar: 1.168502514,
        },
        target: [-0.32, 0.11, 0.48],
        lookAt: 'target',
        duration: 500,
        easing: [0.333333333, 0, 0.666666667, 0.333333333],
      },
    ],
  },
  {
    id: 'preview-turntable',
    loop: true,
    waypoints: [
      {
        orbit: {
          radius: 0.83161041,
          azimuth: -1.370058369,
          polar: 1.168502514,
        },
        target: [-0.32, 0.11, 0.48],
        lookAt: 'target',
        duration: 2142.857142857,
        easing: 'linear',
      },
      {
        orbit: {
          radius: 0.83161041,
          azimuth: 0.200737958,
          polar: 1.168502514,
        },
        target: [-0.32, 0.11, 0.48],
        lookAt: 'target',
        duration: 2142.857142857,
        easing: 'linear',
      },
      {
        orbit: {
          radius: 0.83161041,
          azimuth: 1.771534285,
          polar: 1.168502514,
        },
        target: [-0.32, 0.11, 0.48],
        lookAt: 'target',
        duration: 2142.857142857,
        easing: 'linear',
      },
      {
        orbit: {
          radius: 0.83161041,
          azimuth: 3.342330612,
          polar: 1.168502514,
        },
        target: [-0.32, 0.11, 0.48],
        lookAt: 'target',
        duration: 2142.857142857,
        easing: 'linear',
      },
    ],
  },
  {
    id: 'preview-exit',
    waypoints: [
      {
        target: [-0.711, 0.08, 0.064],
        lookAt: [-0.32, 0.11, 0.48],
        duration: 1000,
        easing: [0.65, 0, 0.35, 1],
      },
    ],
  },
  {
    id: 'preloader-flyover',
    waypoints: [
      {
        duration: 1000,
      },
      {
        kind: 'view',
        position: [-0.74, 0.51, 0.046],
        lookAt: [-0.326, 0.15, 0.48],
        duration: 1700,
        easing: [0.333333333, 0, 0.666666667, 0.396596919],
      },
      {
        kind: 'view',
        position: [-0.566678918, 0.51, -0.06961228],
        lookAt: [-0.326, 0.15, 0.48],
        duration: 438.724059156,
        easing: [0.333333333, 0.318123157, 0.666666667, 0.650675421],
      },
      {
        kind: 'view',
        position: [-0.364185732, 0.51, -0.118783642],
        lookAt: [-0.326, 0.15, 0.48],
        duration: 395.074563419,
        easing: [0.333333333, 0.32232223, 0.666666667, 0.656338567],
      },
      {
        kind: 'view',
        position: [-0.157086784, 0.51, -0.095732859],
        lookAt: [-0.326, 0.15, 0.48],
        duration: 374.01920587,
        easing: [0.333333333, 0.32535038, 0.666666667, 0.659315423],
      },
      {
        kind: 'view',
        position: [0.029638738, 0.51, -0.003240197],
        lookAt: [-0.326, 0.15, 0.48],
        duration: 359.696171882,
        easing: [0.333333333, 0.32763809, 0.666666667, 0.661524886],
      },
      {
        kind: 'view',
        position: [0.173468979, 0.51, 0.147538365],
        lookAt: [-0.326, 0.15, 0.48],
        duration: 350.140500286,
        easing: [0.333333333, 0.3294832, 0.666666667, 0.663314599],
      },
      {
        kind: 'view',
        position: [0.25705589, 0.51, 0.338416707],
        lookAt: [-0.326, 0.15, 0.48],
        duration: 344.289078122,
        easing: [0.333333333, 0.331058835, 0.666666667, 0.664878353],
      },
      {
        kind: 'view',
        position: [0.270317656, 0.51, 0.546372083],
        lookAt: [-0.326, 0.15, 0.48],
        duration: 341.596792363,
        easing: [0.333333333, 0.33250105, 0.666666667, 0.666319792],
      },
      {
        kind: 'view',
        position: [0.211654711, 0.51, 0.746322007],
        lookAt: [-0.326, 0.15, 0.48],
        duration: 341.811810758,
        easing: [0.333333333, 0.333890243, 0.666666667, 0.667742825],
      },
      {
        kind: 'view',
        position: [0.088142674, 0.51, 0.914149566],
        lookAt: [-0.326, 0.15, 0.48],
        duration: 354.647818145,
        easing: [0.333333333, 0.340187767, 0.666666667, 0.664593124],
      },
      {
        kind: 'view',
        position: [-0.903, 0.08, 1.181],
        lookAt: [-0.326, 0.15, 0.48],
        duration: 1700,
        easing: [0.333333333, 0.305367935, 0.666666667, 1],
      },
    ],
  },
];
